import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import {
  trackButtonPressed,
  trackConnectStepRevealed,
} from "../../analytics/tracking";
import { AgentTrafficCard } from "./AgentTrafficCard";
import { CLI_COMMAND_LEAD, CliCommandBlock } from "./CliCommandBlock";
import { PipelineVisual } from "./PipelineVisual";
import { WizardCta } from "./WizardCta";
import { WizardFrame } from "./WizardFrame";
import { useAgentUsedPoll } from "./useAgentUsedPoll";
import { TERMINAL_WAIT_MS, useStalled } from "./waiting";

/** How much of the step is on screen: the command alone, then the wait once it has been
 *  copied — before that there is nothing to wait for. */
type RevealStage = "command" | "waiting";

/* Connect, for the kinds the CLI configures: the command to run. The CLI signs the reader
 * in and creates the key itself. Nothing advances it by hand — the CLI sends the request,
 * so traffic moves it on. */
export function CliHandoffStep({
  command,
  agentId,
  connected = false,
  onConnected,
  onSkip,
  onManual,
  onBack,
}: {
  command: string;
  agentId: string;
  /** Traffic was already seen on an earlier visit, so this is the reader coming back for
   *  the command. Don't auto-advance them off it again. */
  connected?: boolean;
  onConnected: () => void;
  /** Leave for the dashboard without waiting. The agent exists either way. */
  onSkip: () => void;
  onManual?: () => void;
  onBack?: () => void;
}) {
  const { used } = useAgentUsedPoll(agentId);
  const [stage, setStage] = useState<RevealStage>("command");

  /* A reader with traffic, or one coming back from the dashboard or the drawer, has been
     through this once — the reveal is a first-read aid, not a lock. */
  const revealed: RevealStage = connected || used ? "waiting" : stage;
  const waitingShown = revealed === "waiting";

  /* Timed from the reveal, not from mount: the budget is for the terminal detour, and a
     reader still working through the boxes above hasn't started it yet. */
  const stalled = useStalled(used, TERMINAL_WAIT_MS, waitingShown);

  /* Arrival, the funnel's denominator. The stages between this and `connected` are
     each unlocked by a copy button that already reports itself as a ButtonPressed. */
  useEffect(() => {
    if (connected) return;

    trackConnectStepRevealed("command", agentId);
  }, [agentId, connected]);

  useEffect(() => {
    if (!used || connected) return;

    trackConnectStepRevealed("connected", agentId);
    onConnected();
  }, [used, connected, onConnected, agentId]);

  const exitToDashboard = () => {
    trackButtonPressed("Skip to dashboard");
    onSkip();
  };

  return (
    <WizardFrame step={1} lead={CLI_COMMAND_LEAD}>
      <CliCommandBlock
        command={command}
        onCommandCopyPressed={() => setStage("waiting")}
      />

      {/* The wait, said out loud: without it the reader has a command, no feedback, and no
          way to tell that the page moves on by itself. Held back until the command is
          copied — the pipeline's motion would otherwise claim a wait that hasn't begun. */}
      {waitingShown && (
        <>
          <PipelineVisual used={used} />

          <AgentTrafficCard
            used={used}
            stalled={stalled}
            waitingTitle="Waiting for setup to complete in your terminal"
            hint={
              stalled
                ? "If the command didn't work, configure it by hand instead."
                : "When it's done, we'll confirm the connection and take you to your agent's dashboard."
            }
            action={
              stalled ? (
                <button
                  type="button"
                  onClick={onManual ?? exitToDashboard}
                  className="text-[12px] font-medium underline"
                  style={{ color: "var(--fg-2)" }}
                >
                  {onManual ? "Configure manually" : "Exit to dashboard"}
                </button>
              ) : undefined
            }
          />
        </>
      )}

      {/* Only on a return visit: the first pass advances itself, and a Continue here would
          let the reader claim a connection the gateway has not seen. */}
      {connected && (
        <WizardCta
          className="w-full"
          onClick={onConnected}
          iconRight={<ArrowRight />}
        >
          Continue
        </WizardCta>
      )}

      {/* The way out for anyone the CLI did not work for: without it a failed `npx` leaves
          the reader with nowhere to go but back. Once stalled, manual setup moves up into
          the card as the primary fix, and this becomes the exit. */}
      {onManual && (
        <button
          type="button"
          onClick={stalled ? exitToDashboard : onManual}
          className="self-center text-[13px] font-medium underline"
          style={{ color: "#757f8c" }}
        >
          {stalled ? "Exit to dashboard" : "Configure manually instead"}
        </button>
      )}

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center justify-center gap-2 self-center text-[14px] font-medium"
          style={{ color: "#757f8c" }}
        >
          <ArrowLeft size={14} />
          Back
        </button>
      )}
    </WizardFrame>
  );
}
